/**
 * Genuinely-wide display detection (#192).
 *
 * Real PEOC/GOC operator hardware is a standard ~24" landscape monitor plus a
 * portrait secondary — NOT ultrawide. The #131 ultrawide enhancements (docked
 * SITREP reading column, command-bar left-anchor) are a graceful enhancement
 * that must only engage when the display is genuinely wide; at canonical
 * widths (council Part IV region law: 1280–2560) all chrome stays standard.
 *
 * A display is "genuinely wide" when BOTH hold:
 *  - viewport width  > 2560px — the council's canonical band is 1280–2560
 *    inclusive, so a 27" QHD (2560×1440) stays standard;
 *  - aspect ratio   ≥ 2:1   — standard panels top out at 16:9 (≈1.78);
 *    ultrawides start at 21:9 (≈2.33). 2:1 splits the classes with margin on
 *    both sides, so a 16:9 4K monitor at 1× scaling (3840×2160) stays
 *    standard while 21:9 (3440×1440) and 32:9 (3840×1080, 5120×1440) qualify.
 *
 * Note a 2560×1080 ultrawide is wide-shaped but inside the canonical width
 * band — it gets standard chrome (it has no slack pixels to spend on a docked
 * column anyway).
 *
 * WALL profiles (#132/#188/#221) do not use this flag — the wall is its own
 * render profile and stays large-scale regardless of shape.
 */

/** Exclusive lower bound: widths ≤ this are canonical (standard chrome). */
export const WIDE_DISPLAY_MIN_WIDTH = 2560;

/** Inclusive aspect-ratio floor separating ultrawide-class from 16:9/16:10. */
export const WIDE_DISPLAY_MIN_ASPECT = 2;

/**
 * The matchMedia form of the same predicate, for `useMediaQuery`. Keep in
 * lockstep with `isGenuinelyWide` (the pinned unit tests assert the string).
 */
export const WIDE_DISPLAY_QUERY = '(min-width: 2561px) and (min-aspect-ratio: 2/1)';

/** Pure predicate — the testable twin of WIDE_DISPLAY_QUERY. */
export function isGenuinelyWide(width: number, height: number): boolean {
  if (height <= 0) return false;
  return width > WIDE_DISPLAY_MIN_WIDTH && width / height >= WIDE_DISPLAY_MIN_ASPECT;
}
